// RITA-1504 (Nutzungserfassung Phase 2b) — Tabellen-Vorgänge (Entwurf B).
//
// Ein Melder je Tabelle. Er kennt weder React noch das Grid: der Tabellen-Wrapper ruft ihn aus
// den Plugin-Rückrufen auf. Sortieren, Blättern, Seitengröße, Spaltenauswahl und Export melden
// sofort; Filter und Tabellensuche werden je Spalte entprellt (trailing), weil beide an jedem
// Tastendruck hängen und sonst je Zeichen ein Ereignis entstünde.
//
// Zwei Eigenheiten sind wichtig:
//  - Zeitpunkt und Route werden beim BEGINN der Entprellung festgehalten und mit dem Ereignis
//    mitgeschickt. Läuft die Frist erst nach einem Seitenwechsel ab, zählte der Filterwechsel
//    sonst auf der neuen Route — also dort, wo die Tabelle gar nicht steht.
//  - Die Schnittstelle nimmt nur Spaltennamen und Zahlen. Filterwerte und Suchtext erreichen
//    das Erfassungsmodul nie; die Länge der Tabellensuche wird bewusst nicht gemeldet, weil
//    tableProps in batchSchema.ts kein Längenfeld kennt (ein unbekanntes Feld verwürfe das
//    ganze Bündel).
import type { UsageTableName } from '../usageKeys';
import type { UsageEventInput, UsageRecord } from './tracker';
import { buildTableProps } from './usageProps';
import type { TablePropsInput } from './usageProps';

/** Trailing-Frist für Filter und Tabellensuche. */
export const TABLE_DEBOUNCE_MS = 800;
/** Spaltenname der Tabellensuche (sie filtert über alle Spalten). */
export const TABLE_SEARCH_COLUMN = '_suche';

/** Welcher Export-Knopf gedrückt wurde; landet als `column` am Export-Ereignis. */
export type TableExportKind = 'alle' | 'filter' | 'shopify';

/** Sortierung, wie das Grid sie liefert. */
export interface TableSortingEntry {
  columnName: string;
  /** Richtung des Grids; sie wird nicht gemeldet — tableProps kennt in batchSchema.ts kein Feld dafür. */
  direction?: string;
}

/** Rückgabe des Zeitgebers; im Browser eine timeout-Kennung, im Test eine Zahl. */
export type TableTimerHandle = unknown;

export interface TableUsageDeps {
  now?: () => number;
  setTimer?: (handler: () => void, ms: number) => TableTimerHandle;
  clearTimer?: (handle: TableTimerHandle) => void;
  /** Aktuelles Routenmuster; der Wrapper reicht `usageTracker.getRoute` durch. */
  getRoute?: () => string;
}

export interface TableUsageReporter {
  sort(sorting: readonly TableSortingEntry[]): void;
  /** Namen der Spalten, deren Filter sich geändert hat — nie die Filterwerte. */
  filter(columns: readonly string[]): void;
  /** Länge der Eingabe in der Tabellensuche — nie der Suchtext. */
  search(queryLength: number): void;
  page(page: number): void;
  pageSize(pageSize: number): void;
  columns(hiddenCount: number): void;
  exportStarted(kind: TableExportKind): void;
  /** Beim Abbau der Tabelle: schickt Ausstehendes genau einmal ab und meldet danach nicht mehr. */
  dispose(): void;
}

/** Zeitpunkt und Route einer Bedienhandlung (beim Beginn der Entprellung festgehalten). */
interface UsageStamp {
  ts: number;
  route?: string;
}

interface PendingFilter extends UsageStamp {
  handle: TableTimerHandle;
}

export const createTableUsageReporter = (
  record: UsageRecord,
  usageName: UsageTableName,
  deps: TableUsageDeps = {},
): TableUsageReporter => {
  const now = deps.now ?? (() => Date.now());
  const setTimer = deps.setTimer ?? ((handler: () => void, ms: number) => setTimeout(handler, ms));
  const clearTimer =
    deps.clearTimer ?? ((handle: TableTimerHandle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const { getRoute } = deps;

  // Schlüssel ist der rohe Spaltenname: erst buildTableProps bereinigt ihn, und zwei Spalten
  // dürfen sich durch die Bereinigung nicht denselben Eintrag teilen.
  const pending = new Map<string, PendingFilter>();
  let disposed = false;

  const stampNow = (): UsageStamp => {
    const route = getRoute ? getRoute() : undefined;
    return route ? { ts: now(), route } : { ts: now() };
  };

  /** Ein Ereignis darf nie in die Bedienung durchschlagen (Entwurf A.3). */
  const emit = (input: TablePropsInput, stamp: UsageStamp): void => {
    const event: UsageEventInput = { type: 'table', name: usageName, ts: stamp.ts, props: buildTableProps(input) };
    if (stamp.route) event.route = stamp.route;
    try {
      record(event);
    } catch {
      // Erfassung darf die Bedienung nicht stören.
    }
  };

  const emitNow = (input: TablePropsInput): void => {
    if (disposed) return;
    emit(input, stampNow());
  };

  /** Trailing-Entprellung je Spalte; Zeitpunkt und Route stammen vom ersten Anstoß. */
  const scheduleFilter = (column: string): void => {
    if (disposed) return;
    const running = pending.get(column);
    if (running) clearTimer(running.handle);
    const stamp: UsageStamp = running ? { ts: running.ts, route: running.route } : stampNow();
    const handle = setTimer(() => {
      pending.delete(column);
      emit({ op: 'filter', column }, stamp);
    }, TABLE_DEBOUNCE_MS);
    pending.set(column, { ...stamp, handle });
  };

  return {
    sort(sorting: readonly TableSortingEntry[]): void {
      emitNow({ op: 'sort', column: sorting.length > 0 ? sorting[0].columnName : undefined });
    },
    filter(columns: readonly string[]): void {
      columns.forEach((column) => scheduleFilter(column));
    },
    search(queryLength: number): void {
      // Ein leeres Suchfeld ist keine Suche: das Grid meldet den Wert auch beim Zurücksetzen.
      if (!(queryLength > 0)) return;
      scheduleFilter(TABLE_SEARCH_COLUMN);
    },
    page(page: number): void {
      emitNow({ op: 'page', page });
    },
    pageSize(pageSize: number): void {
      emitNow({ op: 'page_size', page_size: pageSize });
    },
    columns(hiddenCount: number): void {
      emitNow({ op: 'columns', hidden_count: hiddenCount });
    },
    exportStarted(kind: TableExportKind): void {
      emitNow({ op: 'export', column: kind });
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      const open = Array.from(pending.entries());
      pending.clear();
      open.forEach(([column, stamp]) => {
        clearTimer(stamp.handle);
        emit({ op: 'filter', column }, stamp);
      });
    },
  };
};
